import axios from "axios";
import ConfigManager from "../utils/configManager";

// API Base URL - mit Runtime-Konfiguration für Server-Deployment
const API_BASE_URL = ConfigManager.getApiBaseUrl();
const API_URL = `${API_BASE_URL}/auth`;

// Axios instance with automatic token handling
const api = axios.create({
  baseURL: API_BASE_URL,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response-Interceptor: Bei 401 nur ausloggen wenn Token ungültig ist
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Log all error responses
    console.error("[API Error] Status:", error.response?.status, "URL:", error.config?.url);
    if (error.response?.data) {
      console.error("[API Error] Response Data:", error.response.data);
    }
    
    // Bei 401 Unauthorized nur ausloggen wenn es sich um eine Token-Ungültigkeit handelt
    if (error.response?.status === 401) {
      const url = error.config?.url || "";

      // Bei 401 Unauthorized nur ausloggen wenn es sich um eine Token-Ungültigkeit handelt
      // 401 bedeutet in der Regel "Nicht authentifiziert" oder "Token abgelaufen"
      // Bei fehlenden Berechtigungen kommt normalerweise 403 Forbidden
      if (
        !url.includes("/auth/login") &&
        !error.config?.skipGlobalErrorHandler
      ) {
        console.warn("401 Unauthorized detected - logging out");
        localStorage.removeItem("authToken");
        localStorage.removeItem("currentUser");

        // Nur weiterleiten wenn wir nicht bereits auf der Login-Seite sind
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      }
    }
    
    // Check for Onboarding Required (409 Conflict)
    if (
      error.response?.status === 409 &&
      error.response?.data?.message === "ONBOARDING_REQUIRED"
    ) {
        const currentUser = localStorage.getItem("currentUser");
        const parsedUser = currentUser ? JSON.parse(currentUser) : null;
        const isRevisor = parsedUser?.roles?.some((role) => {
          const roleName = typeof role === "string" ? role : role?.name || role?.authority;
          return roleName?.toUpperCase().replace(/^ROLE_/, "") === "REVISOR";
        });

        if (parsedUser?.forcePasswordReset || isRevisor) {
          return Promise.reject(error);
        }

        if (window.location.pathname !== "/onboarding/profession-type") {
            window.location.href = "/onboarding/profession-type";
        }
    }
    
    return Promise.reject(error);
  }
);

// Exportiere die API-Instanz für andere Services
export { api };

class AuthService {
  // Registrierung
  async register(userData, endpoint = '/register') {
    try {
      const response = await axios.post(`${API_URL}${endpoint}`, userData);
      return { success: true, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message || "Signup failed",
      };
    }
  }

  // Login
  async login(credentials) {
    try {
      const response = await axios.post(`${API_URL}/login`, credentials);

      if (response.data.token) {
        localStorage.setItem("authToken", response.data.token);
        
        const userData = {
          id: response.data.id,
          username: response.data.username,
          email: response.data.email,
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          roles: response.data.roles || [],
          forcePasswordReset: response.data.forcePasswordReset || false,
        };
        
        localStorage.setItem("currentUser", JSON.stringify(userData));
        
        return { success: true, user: response.data };
      } else {
        console.error('No token received in login response');
        return { success: false, message: "Kein Token erhalten" };
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Login failed",
      };
    }
  }

  // Logout
  logout() {
    localStorage.removeItem("authToken");
    localStorage.removeItem("currentUser");
  }

  // Aktuellen Benutzer abrufen
  getCurrentUser() {
    const user = localStorage.getItem("currentUser");
    return user ? JSON.parse(user) : null;
  }

  // Prüfen ob Benutzer eingeloggt ist
  isAuthenticated() {
    return !!localStorage.getItem("authToken");
  }

  // Token abrufen
  getToken() {
    return localStorage.getItem("authToken");
  }

  // Onboarding
  async submitOnboarding(data) {
    try {
      const response = await api.post("/auth/onboarding", data);
      return { success: true, user: response.data };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Onboarding failed"
      };
    }
  }

  // Password Change
  async changePassword(data) {
    try {
      const response = await api.post("/auth/change-password", data);
      return { success: true, message: response.data.message || "Passwort erfolgreich geändert" };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Fehler beim Ändern des Passworts"
      };
    }
  }

  // Reset user password to the standard onboarding password
  async resetUserPassword(userId) {
    try {
      const response = await api.post(`/admin/students/${userId}/reset-password`);
      return { success: true, message: response.data.message || "Password reset successfully" };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Fehler beim Zurücksetzen des Passworts"
      };
    }
  }

  // Backwards-compatible learner reset endpoint for the student directory
  async resetLearnerPassword(learnerId) {
    try {
      const response = await api.post(`/admin/students/${learnerId}/reset-password`);
      return { success: true, message: response.data.message || "Password reset successfully" };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Fehler beim Zurücksetzen des Passworts"
      };
    }
  }

  // Aktueller Benutzerdaten vom Server abrufen
  async getProfile() {
    try {
      const response = await api.get("/auth/me");
      return { success: true, user: response.data };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Profile could not be loaded",
      };
    }
  }

  async updateLoginPreferences(defaultLandingPage) {
    try {
      const response = await api.patch("/auth/me/preferences", { defaultLandingPage });
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || "Login preference could not be saved" };
    }
  }

  // Alle Benutzer abrufen (nur für Admins)
  async getAllUsers() {
    try {
      const response = await api.get("/users");
      return { success: true, users: response.data };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Users could not be loaded",
      };
    }
  }

  // Benutzerrollen abrufen
  async getUserRoles(userId) {
    try {
      const response = await api.get(`/users/${userId}/roles`);
      return { success: true, roles: response.data };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Roles could not be loaded",
      };
    }
  }

  // Benutzerrollen aktualisieren
  async updateUserRoles(userId, roleNames) {
    try {
      const response = await api.put(`/users/${userId}/roles`, roleNames);
      return { success: true, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Roles could not be updated",
      };
    }
  }

  // Alle verfügbaren Rollen abrufen
  async getAllRoles() {
    try {
      const response = await api.get("/roles");
      return { success: true, roles: response.data };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Roles could not be loaded",
      };
    }
  }

  // Berufsrichtung eines Benutzers aktualisieren
  async updateUserProfession(userId, professionType) {
    const requestData = { professionType: professionType };
    
    try {
      const response = await api.put(`/users/${userId}/profession`, requestData);
      return { success: true, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Profession could not be updated",
      };
    }
  }

  async regenerateGlobalInvitation(role) {
    try {
      const response = await api.post(`/invitations/global/regenerate`, {}, {
        params: { role }
      });
      return { success: true, url: response.data.invitationUrl }; // Adjusted because backend returns InvitationResponse
    } catch (error) {
       return { success: false, message: error.response?.data?.message || "Failed to regenerate global invitation." };
    }
  }

  // Invitation Methods
  async getActiveInvitations() {
    try {
      const response = await api.get("/invitations");
      return { success: true, invitations: response.data };
    } catch (error) {
       return { success: false, message: error.response?.data?.message || "Failed to fetch invitations" };
    }
  }

  async validateInvitationToken(token) {
    try {
      const response = await api.get(`/invitations/${token}/validate`);
      return response.data;
    } catch (error) {
       throw error.response?.data || { message: "Validation failed" };
    }
  }

  async deleteInvitation(token) {
    try {
      await api.delete(`/invitations/${token}`);
      return { success: true };
    } catch (error) {
      console.error("Delete failed:", error);
      return { success: false, message: error.response?.data?.message || "Failed to delete invitation" };
    }
  }

  async createInvitation(invitationData) {
     try {
       const response = await api.post(`/invitations`, invitationData);
       return { success: true, ...response.data };
     } catch (error) {
       return { success: false, message: error.response?.data?.message || "Creation failed" };
     }
  }

  // Look up a practice instructor by email or username (admin only)
  async lookupPracticeInstructor(identifier) {
    try {
      const response = await api.get("/users/lookup-practice-instructor", {
        params: { identifier },
      });
      return { success: true, user: response.data };
    } catch (error) {
      if (error.response?.status === 404) {
        return { success: false, notFound: true };
      }
      return {
        success: false,
        message:
          error.response?.data?.message || "Search failed. Please try again.",
      };
    }
  }

  // Benutzer löschen - nur für Admins
  async deleteUser(userId) {
    try {
      const response = await api.delete(`/users/${userId}`);
      return { success: true, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "User could not be deleted",
        // Set when the user still owns data: the caller may offer a second
        // confirmation that permanently erases the user together with it.
        requiresForce: error.response?.data?.requiresForce === true,
        relatedData: error.response?.data?.relatedData || null,
      };
    }
  }

  // FORCE Benutzer löschen (Cascading) - nur für Admins
  async forceDeleteUser(userId) {
    try {
      const response = await api.delete(`/users/${userId}/force`);
      return {
        success: true,
        message: response.data.message,
        deleted: response.data.deleted || null,
        warnings: response.data.warnings || [],
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "User could not be permanently deleted",
      };
    }
  }

  // Validate invitation token
  async validateInvitation(token) {
    try {
      const response = await api.get(`/invitations/${token}/validate`);
      return { 
        success: response.data.valid, 
        role: response.data.role, 
        professionType: response.data.professionType,
        message: response.data.message 
      };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || "Invalid invitation token" 
      };
    }
  }

  // Register with invitation token
  async registerWithInvitation(token, userData) {
    try {
      const response = await api.post(`/invitations/${token}/register`, userData);
      if (response.data.token) {
        localStorage.setItem("authToken", response.data.token);

        const currentUser = {
          id: response.data.id,
          username: response.data.username,
          email: response.data.email,
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          roles: response.data.roles || [],
          forcePasswordReset: response.data.forcePasswordReset || false,
        };

        localStorage.setItem("currentUser", JSON.stringify(currentUser));
      }

      return { 
          success: true, 
          message: response.data.message,
          username: response.data.username,
          email: response.data.email,
          token: response.data.token,
          user: response.data.token
            ? {
                id: response.data.id,
                username: response.data.username,
                email: response.data.email,
                firstName: response.data.firstName,
                lastName: response.data.lastName,
                roles: response.data.roles || [],
                forcePasswordReset: response.data.forcePasswordReset || false,
              }
            : null,
      };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || "Registration failed" 
      };
    }
  }
}

// Export sowohl der Klasse als auch der konfigurierten API-Instanz
const authService = new AuthService();
export default authService;
